import React, { useState, useEffect, useCallback } from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  Building2,
  Calendar,
  Eye,
  AlertCircle,
  Loader2,
  Inbox
} from 'lucide-react';
import {
  getPendingApprovalProfiles,
  approveProfile,
  rejectProfile,
  PublicProfileView
} from '../../../api/PublicProfile/publicProfileApi';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../../../components/ui/Dialog';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';

interface ApprovalTabProps {
  selectedPersonaId: string;
  onSettingsChange?: () => void;
}

interface RejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}

const RejectModal: React.FC<RejectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    onConfirm(reason);
    setReason('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('Confirm Rejection')}</DialogTitle>
          <DialogDescription>{t('Are you sure you want to reject this profile?')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-xs text-muted-foreground mb-4">
            {t('The profile will be returned to draft status.')}
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('Rejection Reason')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('Enter rejection reason...')}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('Please provide a reason for rejecting this profile')}
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            {t('Cancel')}
          </Button>
          <Button variant="destructive" loading={isLoading} onClick={handleConfirm} iconName="XCircle">
            {t('Reject Profile')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ApproveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

const ApproveModal: React.FC<ApproveModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('Confirm Approval')}</DialogTitle>
          <DialogDescription>{t('Are you sure you want to approve this profile?')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-xs text-muted-foreground">
            {t('This will make the profile publicly visible.')}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            {t('Cancel')}
          </Button>
          <Button variant="success" loading={isLoading} onClick={onConfirm} iconName="CheckCircle">
            {t('Approve Profile')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ProfileCardProps {
  profile: PublicProfileView;
  onApprove: (profile: PublicProfileView) => void;
  onReject: (profile: PublicProfileView) => void;
  onView: (profile: PublicProfileView) => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  onApprove,
  onReject,
  onView
}) => {
  const { t } = useTranslation();

  const getInitials = () => {
    if (profile.customJobTitle) {
      return profile.customJobTitle.charAt(0).toUpperCase();
    }
    return 'P';
  };

  return (
    <Card variant="interactive">
      <CardContent>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold">
              {getInitials()}
            </div>
            <div>
              <h3 className="font-semibold text-foreground">
                {profile.customJobTitle || t('Profile')} #{profile.id}
              </h3>
              <p className="text-sm text-muted-foreground">
                {profile.slug}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">
            <Clock className="w-3 h-3" />
            {t('Pending Approval')}
          </span>
        </div>

        {/* Details */}
        <div className="space-y-2 mb-4">
          {profile.personaName && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span>{t('Persona')}: {profile.personaName}</span>
            </div>
          )}
          {profile.customLocation && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building2 className="w-4 h-4" />
              <span>{profile.customLocation}</span>
            </div>
          )}
          {profile.createdOn && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{t('Submitted On')}: {appDate(parseApiUtc(profile.createdOn))}</span>
            </div>
          )}
          {profile.bio && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {profile.bio}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-4 border-t border-border">
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 min-h-[44px]"
            onClick={() => onView(profile)}
            iconName="Eye"
          >
            {t('View')}
          </Button>
          <Button
            variant="success"
            size="sm"
            className="flex-1 min-h-[44px]"
            onClick={() => onApprove(profile)}
            iconName="CheckCircle"
          >
            {t('Approve')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="flex-1 min-h-[44px]"
            onClick={() => onReject(profile)}
            iconName="XCircle"
          >
            {t('Reject')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ApprovalTab: React.FC<ApprovalTabProps> = ({ selectedPersonaId, onSettingsChange }) => {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<PublicProfileView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<PublicProfileView | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchProfiles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getPendingApprovalProfiles();
      if (result.status === 200 && result.payload) {
        setProfiles(result.payload);
      } else {
        setError(result.errors?.[0]?.description || t('Failed to load pending profiles'));
      }
    } catch (err) {
      setError(t('Failed to load pending profiles'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleApproveClick = (profile: PublicProfileView) => {
    setSelectedProfile(profile);
    setApproveModalOpen(true);
  };

  const handleRejectClick = (profile: PublicProfileView) => {
    setSelectedProfile(profile);
    setRejectModalOpen(true);
  };

  const handleViewProfile = (profile: PublicProfileView) => {
    if (profile.slug) {
      window.open(`/p/${profile.slug}`, '_blank');
    }
  };

  const handleApproveConfirm = async () => {
    if (!selectedProfile) return;

    try {
      setIsSubmitting(true);
      const result = await approveProfile(selectedProfile.id);
      if (result.status === 200) {
        showToast('success', t('Profile Approved'));
        setProfiles(profiles.filter(p => p.id !== selectedProfile.id));
        onSettingsChange?.();
      } else {
        showToast('error', result.errors?.[0]?.description || t('Failed to approve profile'));
      }
    } catch (err) {
      showToast('error', t('Failed to approve profile'));
    } finally {
      setIsSubmitting(false);
      setApproveModalOpen(false);
      setSelectedProfile(null);
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!selectedProfile) return;

    try {
      setIsSubmitting(true);
      const result = await rejectProfile(selectedProfile.id, reason || undefined);
      if (result.status === 200) {
        showToast('success', t('Profile Rejected'));
        setProfiles(profiles.filter(p => p.id !== selectedProfile.id));
        onSettingsChange?.();
      } else {
        showToast('error', result.errors?.[0]?.description || t('Failed to reject profile'));
      }
    } catch (err) {
      showToast('error', t('Failed to reject profile'));
    } finally {
      setIsSubmitting(false);
      setRejectModalOpen(false);
      setSelectedProfile(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2
                        ${toast.type === 'success'
                          ? 'bg-success/10 text-success'
                          : 'bg-destructive/10 text-destructive'}`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <Card variant="elevated">
        <CardContent>
          <h2 className="text-lg font-semibold text-foreground mb-2">
            {t('Pending Profile Approvals')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('Review and approve or reject employee public profiles before they go live.')}
          </p>
          {profiles.length > 0 && (
            <p className="text-sm text-warning mt-2 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {profiles.length} {t('profile(s) pending approval')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card variant="elevated">
          <CardContent className="p-12 flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">{t('Loading pending profiles...')}</p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card variant="elevated">
          <CardContent className="p-12 flex flex-col items-center justify-center">
            <AlertCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-destructive mb-4">{error}</p>
            <Button variant="default" onClick={fetchProfiles}>
              {t('Try Again')}
            </Button>
          </CardContent>
        </Card>
      ) : profiles.length === 0 ? (
        <Card variant="elevated">
          <CardContent className="p-12 flex flex-col items-center justify-center">
            <Inbox className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('No Pending Approvals')}
            </h3>
            <p className="text-muted-foreground text-center">
              {t('There are no profiles waiting for approval. When employees submit profiles for review, they will appear here.')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {profiles.map((profile) => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              onApprove={handleApproveClick}
              onReject={handleRejectClick}
              onView={handleViewProfile}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <ApproveModal
        isOpen={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedProfile(null);
        }}
        onConfirm={handleApproveConfirm}
        isLoading={isSubmitting}
      />

      <RejectModal
        isOpen={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedProfile(null);
        }}
        onConfirm={handleRejectConfirm}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default ApprovalTab;
